import type { TimelineBlock } from "../api/types";
import {
  LABEL_MIN_PX,
  LIVE_EXTEND_MAX_MS,
  TICK_MAX_PX,
  TRACK_MERGE_SPAN_MS,
  type Placed,
  type View,
  place,
  timeToX,
  viewSpanMs,
} from "./geometry";
import { type Bands, bands, theme } from "./theme";
import { drawGhost, drawShow, drawTick, drawTrack } from "./draw/blocks";
import { drawChatDensity, drawGrid, drawRuler, drawWaveform } from "./draw/lanes";
import {
  drawArchived,
  drawGaps,
  drawListened,
  drawLiveDelay,
  drawNowMarker,
  drawPlayhead,
  drawSnapMark,
  drawVoid,
} from "./draw/overlays";
import { fillRuns, seam, showFill, trackFill } from "./draw/runs";
import type { DrawInput, Scene } from "./draw/types";

export type { DrawInput, Scene } from "./draw/types";
export { ellipsise, nameSpanPx } from "./draw/text";

/**
 * Render one frame and return what was placed, so the pointer handlers hit-test exactly
 * what is on screen rather than a second, possibly divergent, computation of it.
 */
export function draw(ctx: CanvasRenderingContext2D, input: DrawInput): Scene {
  const { view } = input;
  // The bands decide the height, not a caller: the canvas element is sized from the same
  // function, so there is no arrangement in which what is painted and what was measured
  // disagree.
  const b = bands({ chat: input.chatLane, compact: input.compact ?? false });
  const height = b.chatBottom;

  drawBackdrop(ctx, input, b, height);
  const shows = drawShows(ctx, input, b);

  // Tracks come back individually only when the server judged them wide enough to draw;
  // otherwise every entry is a bucket standing for several and cannot be selected.
  const ticksOnly = input.tracks.some((t) => t.id === null);
  const tracks = placeTracks(view, trackRows(input, ticksOnly), shows);
  drawTracks(ctx, input, b, tracks, ticksOnly);
  drawMarkings(ctx, input, b, height);

  return { shows, tracks, ticksOnly, bands: b };
}

/** The well, the envelope behind it, the gridlines, and the two washes laid over them. */
function drawBackdrop(
  ctx: CanvasRenderingContext2D,
  input: DrawInput,
  b: Bands,
  height: number,
): void {
  const { view } = input;
  ctx.clearRect(0, 0, view.width, height);
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, view.width, height);
  if (input.waveform) drawWaveform(ctx, view, input.waveform, b);

  drawGrid(ctx, view, height, b);
  // Archived first. The server hands these disjoint, so the order is only belt and braces:
  // wherever a hole and a band did meet, the hole is the stronger claim and should survive.
  drawArchived(ctx, view, input.archived, b);
  drawGaps(ctx, view, input.gaps, input.nowMs, b);
}

/** The show lane, with the listened tracker and the snap mark that are drawn against it. */
function drawShows(
  ctx: CanvasRenderingContext2D,
  input: DrawInput,
  b: Bands,
): Placed<TimelineBlock>[] {
  const { view } = input;
  // While a block is being dragged the draft span stands in for the stored one, so the block
  // tracks the pointer without anything being written until the drag ends.
  const draft = input.draft;
  const sourceShows = draft
    ? input.shows.map((block) =>
        block.id === draft.id ? { ...block, start: draft.start, end: draft.end } : block,
      )
    : input.shows;

  const shows = place(view, sourceShows, 2, input.nowMs);
  fillRuns(ctx, shows, "show", LABEL_MIN_PX, b.showTop, b.showBottom - b.showTop, view.width, (p) =>
    showFill(p.item, input.hoverId),
  );
  for (let i = 0; i < shows.length; i++) {
    drawShow(ctx, shows[i]!, b, input, seam(shows, i, "show"), seam(shows, i + 1, "show"));
  }
  if (input.listened) {
    drawListened(ctx, view, shows, input.listened, input.listenedNow, input.listeningMs, b);
  }
  if (input.snapMs != null) drawSnapMark(ctx, view, input.snapMs, b);
  return shows;
}

/**
 * The newest track the live block has not taken over, and how far in front of it the title log
 * says it actually ran.
 *
 * The segmenter closes whichever track it finds running at the live edge of the pass that is
 * running, so between passes that stored end stops short of the moment the title really
 * changed, and a strip of empty timeline opens between the last block and the ghost naming
 * what replaced it. The ghost's start is that moment, so it is also where the block before it
 * ends.
 *
 * Found by end rather than taken as the last element: the order of the array is the server's
 * business, and stretching the wrong block would drive a border through a neighbour.
 */
function newestTail(
  sourceTracks: readonly TimelineBlock[],
  shows: readonly TimelineBlock[],
  liveStartMs: number,
): { newest: TimelineBlock | null; tailMs: number } {
  let newest: TimelineBlock | null = null;
  let newestEndMs = -Infinity;
  for (const track of sourceTracks) {
    const end = Date.parse(track.end);
    if (end > newestEndMs) {
      newestEndMs = end;
      newest = track;
    }
  }
  // Never past a block that already follows it. The strip being closed is empty airtime
  // between the last segmented track and the ghost; where a show already begins there is no
  // strip, and `newest` is only the last row of the *fetched* window, which in a window
  // scrolled back through the archive is an ordinary track with a show hard against its end.
  // Both lanes share one band (theme.bands) and this one is painted second, so the stretch
  // landed as three minutes of jukebox over the front of that show.
  let followingMs = Number.POSITIVE_INFINITY;
  for (const show of shows) {
    const start = Date.parse(show.start);
    if (start >= newestEndMs && start < followingMs) followingMs = start;
  }
  // Capped at one pass plus slack, like every other extension here. A worker that has been
  // down for an hour leaves an hour of unsegmented airtime holding many tracks, and papering
  // over that with one enormous block would hide the outage rather than show it.
  return { newest, tailMs: Math.min(liveStartMs, newestEndMs + LIVE_EXTEND_MAX_MS, followingMs) };
}

/**
 * The jukebox rows to place: everything the live block has not taken over, with the one that
 * abuts the ghost stretched forward to meet it.
 *
 * How far back the live block reaches is what decides which rows it speaks for. A row starting
 * inside its span is the same track: the worker has written the stretch of it that had played
 * by the last pass, and drawing that as a settled block beside the live one cuts a single track
 * into a recorded half and a live half. So it is left out here and never drawn.
 *
 * Stretched to the ghost rather than to the clock, and only for the block that abuts it: the
 * strip being closed is the difference between where the last pass ended this row and where
 * the title actually changed. Every earlier row already ends where its successor begins.
 *
 * Rewritten onto the row rather than onto its placement, the way the draft above is: culling
 * reads the span it is handed, so a stored end that has scrolled off the left of the viewport
 * dropped the whole block -- stretch and all -- while that stretch was still filling the lane.
 * That is the bug this shape exists for, and it also keeps the seam test honest, which reads a
 * block's width off its neighbour and would judge a pre-stretch one too narrow for a boundary
 * it has in fact grown wide enough to hold.
 */
function trackRows(input: DrawInput, ticksOnly: boolean): readonly TimelineBlock[] {
  const liveStartMs =
    !ticksOnly && input.liveTrack ? Date.parse(input.liveTrack.start) : Number.POSITIVE_INFINITY;
  const sourceTracks = Number.isFinite(liveStartMs)
    ? input.tracks.filter((track) => Date.parse(track.start) < liveStartMs)
    : input.tracks;
  if (ticksOnly || !input.liveTrack) return sourceTracks;
  const { newest, tailMs } = newestTail(sourceTracks, input.shows, liveStartMs);
  return newest && tailMs > Date.parse(newest.end)
    ? sourceTracks.map((track) =>
        track === newest ? { ...track, end: new Date(tailMs).toISOString() } : track,
      )
    : sourceTracks;
}

/**
 * No clock here, unlike the shows above: `liveEndMs` would draw a block on air out to the
 * present, and in this lane the ghost is what covers the present. The row for the track that is
 * playing was left out of `trackRows` for that same reason, so everything placed here has ended.
 */
function placeTracks(
  view: View,
  rows: readonly TimelineBlock[],
  shows: readonly Placed<TimelineBlock>[],
): Placed<TimelineBlock>[] {
  const tracks = place(view, rows, 1);

  // Give back the widening `place` added where it lands on a show. A block narrower than a
  // pixel is grown rightwards so it does not vanish, and the pixel it takes is the show's
  // leading edge -- one band, jukebox painted second. The lane loses nothing: the tracks
  // behind it already reach the same boundary. Only the widening is given back, so a track
  // that genuinely runs into a show still draws as the overlap it is, and the parse is paid
  // only by a block that actually collides.
  for (let i = 0, s = 0; i < tracks.length; i++) {
    const track = tracks[i]!;
    while (s < shows.length && shows[s]!.x0 <= track.x0) s++;
    const next = shows[s];
    if (!next || track.x1 <= next.x0) continue;
    // `<=`, because abutting is the ordinary case: a track ends exactly where the show after it
    // begins. Strictly less would only ever clamp a block that stops short of one.
    if (timeToX(view, Date.parse(track.item.end)) <= next.x0) track.x1 = next.x0;
  }
  return tracks;
}

/** The jukebox lane: its fills, its blocks or tick marks, and the ghost on the end of it. */
function drawTracks(
  ctx: CanvasRenderingContext2D,
  input: DrawInput,
  b: Bands,
  tracks: Placed<TimelineBlock>[],
  ticksOnly: boolean,
): void {
  const { view } = input;
  // Past the merge span the lane is one section rather than a row of blocks, so nothing in it
  // earns a name or an edge of its own: `Infinity` is that, said once, in the units both rules
  // already speak. Below it a track earns both at the width it becomes readable.
  const merged = viewSpanMs(view) > TRACK_MERGE_SPAN_MS;
  const trackMinPx = merged ? Infinity : LABEL_MIN_PX;

  // A tick stands for a track too narrow to draw as a block, whether the server bucketed the
  // lane or the zoom simply squeezed it. A merged lane has no blocks for one to be too narrow
  // for, and a bright bar per sliver is the picket fence again in another colour, so inside one
  // a short track -- or a bucket standing for several -- is simply part of the section.
  //
  // `merged` first, and that is the whole of it: a lane wide enough to bucket is a lane far
  // past the merge span, so asking `ticksOnly` before it meant the widest zooms, the ones the
  // merge exists for, were the only ones that never reached it.
  const tick = (placed: Placed<TimelineBlock>) =>
    !merged && (ticksOnly || placed.x1 - placed.x0 <= TICK_MAX_PX);

  fillRuns(
    ctx,
    tracks,
    "track",
    trackMinPx,
    b.trackTop,
    b.trackBottom - b.trackTop,
    view.width,
    (p) => (tick(p) ? null : trackFill(p.item, input.hoverId)),
  );

  for (let i = 0; i < tracks.length; i++) {
    const item = tracks[i]!;
    if (tick(item)) {
      drawTick(ctx, item, b, input);
      continue;
    }
    drawTrack(
      ctx,
      item,
      b,
      input,
      seam(tracks, i, "track", trackMinPx),
      seam(tracks, i + 1, "track", trackMinPx),
      trackMinPx,
    );
  }

  // After `ticksOnly`, never before: the ghost has no id, and folding it into the array above
  // would make every real track in the lane collapse to a tick mark.
  if (input.liveTrack) {
    const [ghost] = place(view, [input.liveTrack], 1, input.nowMs);
    // Onto the scene as well as onto the canvas, so it is hoverable and clickable like anything
    // else drawn. Its null id sends a click down the same path a bucket takes.
    if (ghost) {
      drawGhost(ctx, ghost, b, input);
      tracks.push(ghost);
    }
  }
}

/** The chat lane, the delay band, the void, and the ruler and markers over all of it. */
function drawMarkings(
  ctx: CanvasRenderingContext2D,
  input: DrawInput,
  b: Bands,
  height: number,
): void {
  const { view, zone } = input;
  drawChatDensity(ctx, view, input.chat, input.chatGaps, b);

  // Over the blocks, because it is a claim about all of them at once, and under the ruler and
  // the two markers, which are what it is measured between. The chat lane is left out for the
  // same reason the gaps leave it out: the logger is not the recorder and is not held back.
  if (input.liveDelayFromMs !== null) {
    drawLiveDelay(ctx, view, input.liveDelayFromMs, input.nowMs, b);
  }

  // Last of the content markings and under the ruler and the two markers, for the same reason
  // the delay band is: those three are what the void is measured against.
  drawVoid(ctx, view, input.archiveStartMs, input.nowMs, b);

  drawRuler(ctx, view, zone, b);
  if (input.nowMs !== input.playheadMs) drawNowMarker(ctx, view, input.nowMs, height);
  drawPlayhead(ctx, view, input.playheadMs, height);
}
